import { getLocalForage } from './local-forage';

const IDB_KEY = {
    search: 'filter:search',
    trending: 'filter:trending',
    recommend: 'filter:recommend',
    explore: 'filter:explore',
};

// One record per filter list. Both parts use the usual { data, meta } shape, and each meta is the hash of its own data:
//   filters       { data: [ ...filters ],                    meta }
//   filterOptions { data: [ { _id, hash, data } ... ],       meta }
//                 the options those filters reference; `data` is the option body, null until the user opens it
async function getFilterRecord(group) {
    const empty = { filters: { data: null, meta: null }, filterOptions: { data: [], meta: null } };
    try {
        const rec = await (await getLocalForage()).getItem(IDB_KEY[group]);
        if (!rec) return empty;
        if (rec.filters) {
            return { filters: rec.filters, filterOptions: rec.filterOptions ?? empty.filterOptions };
        }
        // older records: { data: [filters], meta } or { data: { filters, filterOptions }, meta }.
        // Their meta hashes use the old method, so the server just sends everything once.
        const d = rec.data;
        return {
            filters: { data: Array.isArray(d) ? d : (d?.filters ?? null), meta: rec.meta ?? null },
            filterOptions: { data: Array.isArray(d) ? [] : (d?.filterOptions ?? []), meta: null },
        };
    } catch {
        return empty;
    }
}

async function setFilterRecord(group, { filters, filterOptions }) {
    try {
        await (await getLocalForage()).setItem(IDB_KEY[group], { filters, filterOptions });
    } catch {
        // ignore write errors
    }
}

// Read-modify-write of one record, one at a time per group. The record is read when its turn comes, so a write
// never overwrites a change made by an earlier one (several option downloads and a list response can overlap).
// `mutate(record)` returns the record to store, or null to leave it alone.
const queues = {};
function updateFilterRecord(group, mutate) {
    const run = (queues[group] ?? Promise.resolve()).then(async () => {
        const next = mutate(await getFilterRecord(group));
        if (next) await setFilterRecord(group, next);
    });
    queues[group] = run.catch(() => {});
    return run;
}

// Option bodies used to be cached under their own keys (`filter:option:{id}`, then `filterOption:{group}`);
// drop those leftovers once per session.
let legacyKeysRemoved = false;
async function removeLegacyFilterOptionKeys() {
    if (legacyKeysRemoved) return;
    legacyKeysRemoved = true;
    try {
        const localForage = await getLocalForage();
        const keys = await localForage.keys();
        const legacy = keys.filter((k) => k.startsWith('filter:option:') || k.startsWith('filterOption:'));
        await Promise.all(legacy.map((k) => localForage.removeItem(k)));
    } catch {
        // ignore
    }
}

export { getFilterRecord, updateFilterRecord, removeLegacyFilterOptionKeys };
